/**
 * Email Templates — static, preview-only metadata (Phase 1).
 *
 * The live API returns templates with { uuid, name, label, subject, greeting,
 * body_text, placeholders } but no category / cta / data-block. The 19 template
 * `name`s are stable, so we map them here purely to drive grouping and the live
 * email preview. None of this is persisted.
 *
 * Phase 2: once the API returns `category`, prefer the server value and keep this
 * map as a fallback (see metaFor()).
 */

export interface CategoryDef {
  label: string;
  icon: string;   // bootstrap-icons class, e.g. 'bi-clock-history'
  class: string;  // css modifier on .g-ico / .cat-pill
}

export interface DataBlock {
  title: string;
  sub: string;
}

export interface TemplateMeta {
  category: string;
  cta: string;
  dataBlock: DataBlock | null;
}

export const CATEGORIES: { [key: string]: CategoryDef } = {
  timesheet:  { label: 'Timesheet',  icon: 'bi-clock-history', class: 'timesheet' },
  invoice:    { label: 'Invoice',    icon: 'bi-receipt',       class: 'invoice' },
  account:    { label: 'Account',    icon: 'bi-person-circle', class: 'account' },
  job:        { label: 'Job',        icon: 'bi-briefcase',     class: 'job' },
  onboarding: { label: 'Onboarding', icon: 'bi-person-check',  class: 'onboarding' },
  other:      { label: 'Other',      icon: 'bi-three-dots',    class: 'other' },
};

export const CATEGORY_ORDER: string[] = [
  'timesheet', 'invoice', 'account', 'job', 'onboarding', 'other',
];

const TEMPLATE_META: { [name: string]: TemplateMeta } = {
  // ---- Timesheet ----
  timesheet_submitted:    { category: 'timesheet', cta: 'Review timesheet',      dataBlock: { title: 'Timesheet summary table', sub: '5 shifts · {total_hours} hrs · Week ending {end_date}' } },
  timesheet_approved:     { category: 'timesheet', cta: 'View timesheet',        dataBlock: { title: 'Timesheet details', sub: '{total_hours} hrs approved · Week ending {end_date}' } },
  timesheet_rejected:     { category: 'timesheet', cta: 'Resubmit timesheet',    dataBlock: { title: 'Rejection details', sub: 'Week ending {end_date} · Reason from approver' } },
  timesheet_bulk_email:   { category: 'timesheet', cta: 'Approve all timesheets', dataBlock: { title: 'Bulk timesheet digest', sub: 'Multiple contractors · Week ending {end_date}' } },
  timesheet_import_status:{ category: 'timesheet', cta: 'View import',           dataBlock: { title: 'Import status report', sub: 'Rows imported, skipped and failed' } },

  // ---- Invoice ----
  invoice_notification:   { category: 'invoice',   cta: 'View invoice',          dataBlock: { title: 'Invoice summary', sub: '{invoice_number} · {invoice_amount} · Due {due_date}' } },

  // ---- Account ----
  welcome_email:          { category: 'account',   cta: 'Activate my account',   dataBlock: { title: 'Getting started checklist', sub: 'First-login walkthrough' } },
  reset_password:         { category: 'account',   cta: 'Reset password',        dataBlock: null },

  // ---- Job ----
  job_assigned:           { category: 'job',       cta: 'View assignment',       dataBlock: { title: 'Assignment details card', sub: '{job_name} · {client_name} · Starting {start_date}' } },
  contract_received:      { category: 'job',       cta: 'Review contract',       dataBlock: { title: 'Contract PDF attached', sub: 'Ready for your signature' } },
  signed_contract_submitted: { category: 'job',    cta: 'View signed contract',  dataBlock: { title: 'Signed contract PDF', sub: 'Submitted by {contractor_name}' } },

  // ---- Onboarding ----
  onboarding_approved_assigned:        { category: 'onboarding', cta: 'View next steps',      dataBlock: { title: 'First-day checklist', sub: 'Documents · ID checks · Start date {start_date}' } },
  onboarding_details_submitted:        { category: 'onboarding', cta: 'Review submission',    dataBlock: { title: 'Onboarding form details', sub: 'Submitted by {contractor_name}' } },
  onboarding_details_resubmitted:      { category: 'onboarding', cta: 'Review resubmission',  dataBlock: { title: 'Updated onboarding details', sub: 'Resubmitted by {contractor_name}' } },
  onboarding_details_rejected:         { category: 'onboarding', cta: 'Update details',       dataBlock: { title: 'Items needing attention', sub: 'Specific fields highlighted' } },
  onboarding_details_rejected_permanent: { category: 'onboarding', cta: 'Contact us',         dataBlock: { title: 'Closed — onboarding ended', sub: 'No further action available' } },

  // ---- Other ----
  candidate_import_status: { category: 'other',    cta: 'View import',           dataBlock: { title: 'Import status report', sub: 'Rows imported, skipped and failed' } },
  general_email:           { category: 'other',    cta: 'Open Clug',             dataBlock: null },
  reminder:                { category: 'other',    cta: 'Take action',           dataBlock: { title: 'Reminder context', sub: "What's pending and why we're nudging" } },
};

const FALLBACK_META: TemplateMeta = { category: 'other', cta: 'Open Clug', dataBlock: null };

export function metaFor(name: string): TemplateMeta {
  return TEMPLATE_META[name] || FALLBACK_META;
}

/** Static sample data for the live preview only. */
export const PREVIEW_SAMPLE: { [key: string]: string } = {
  agency_name: 'Hyphen Talent',
  agency_email: 'hello@hyphen.co',
  user_name: 'Priya Joshi',
  contractor_name: 'Priya Joshi',
  contractor_first_name: 'Priya',
  job_name: 'Senior Backend Engineer',
  client_name: 'Northwind Trading',
  start_date: 'Mon 18 May 2026',
  end_date: 'Sun 24 May 2026',
  week_ending: '24 May 2026',
  total_hours: '37.5',
  invoice_number: 'INV-2026-0182',
  invoice_amount: '£4,820.00',
  due_date: '07 Jun 2026',
  rejection_reason: "Tuesday hours don't match the approved roster",
  action_url: 'https://app.clug.io/a/…',
  expires_in: '72 hours',
  admin_name: 'Sam Mehta',
};
